/**
 * @fileoverview State and derived values for the Reports page.
 * @module pages/reports/useReportsContext
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { getReportsOptions } from "../../data/selectOptions";
import { canAccessReports } from "../../utils/accessControl";
import useRequireAccess from "../../hooks/useRequireAccess";
import { isReportDownloadEnabled } from "../../utils/reportDownloadRules.js";
import {
  REPORT_I18N_KEYS,
  REPORT_YEAR_START,
} from "../../utils/reportConstants.js";

const buildYearOptions = () =>
  Array.from(
    { length: new Date().getFullYear() - REPORT_YEAR_START + 1 },
    (_, index) => String(REPORT_YEAR_START + index),
  );

const eventSelectProps = {
  getOptionValue: (event) => event._id,
  getOptionLabel: (event) => event.nombre,
};

/** Holds Reports page state, access guard, and derived selector props. */
const useReportsContext = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toastId = useRef(null);
  const { allowed } = useRequireAccess(canAccessReports);

  const [selectedReportOption, setSelectedReportOption] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMemberCompany, setSelectedMemberCompany] = useState(null);
  const [selectedBuyerCompanies, setSelectedBuyerCompanies] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
  const [salaLayouts, setSalaLayouts] = useState([]);
  const [buyerCompaniesData, setBuyerCompaniesData] = useState([]);
  const [memberCompaniesOptions, setMemberCompaniesOptions] = useState([]);
  const [allBuyerCompanies, setAllBuyerCompanies] = useState([]);
  const [buyerCompaniesOptions, setBuyerCompaniesOptions] = useState([]);
  const [eventsOptions, setEventsOptions] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingMemberCompanies, setLoadingMemberCompanies] = useState(false);
  const [loadingBuyerCompanies, setLoadingBuyerCompanies] = useState(false);
  const [loadingExportLogs, setLoadingExportLogs] = useState(false);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [loadingB2bEvents, setLoadingB2bEvents] = useState(false);
  const [b2bEventsOptions, setB2bEventsOptions] = useState([]);
  const [b2bBuyerCompaniesOptions, setB2bBuyerCompaniesOptions] = useState([]);
  const [allB2bBuyerCompanies, setAllB2bBuyerCompanies] = useState([]);
  const [b2bBuyerUsersOptions, setB2bBuyerUsersOptions] = useState([]);
  const [selectedB2bEvent, setSelectedB2bEvent] = useState(null);
  const [selectedB2bBuyerCompanies, setSelectedB2bBuyerCompanies] = useState(
    [],
  );
  const [selectedB2bBuyerUser, setSelectedB2bBuyerUser] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [allEventsOptions, setAllEventsOptions] = useState([]);
  const [billed, setBilled] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  const yearOptions = buildYearOptions();
  const reportsOptions = getReportsOptions(t);

  const isDownloadEnabled = () =>
    isReportDownloadEnabled(selectedReportOption?.value, {
      selectedMemberCompany,
      startDate,
      endDate,
      selectedEvent,
      selectedBuyerCompanies,
      selectedB2bEvent,
      selectedYear,
      selectedEventTypes,
    });

  return {
    allowed,
    navigate,
    t,
    i18n,
    toastId,
    selectedReportOption,
    setSelectedReportOption,
    selectedEvent,
    setSelectedEvent,
    selectedMemberCompany,
    setSelectedMemberCompany,
    selectedBuyerCompanies,
    setSelectedBuyerCompanies,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isStartCalendarOpen,
    setIsStartCalendarOpen,
    isEndCalendarOpen,
    setIsEndCalendarOpen,
    salaLayouts,
    setSalaLayouts,
    buyerCompaniesData,
    setBuyerCompaniesData,
    memberCompaniesOptions,
    setMemberCompaniesOptions,
    allBuyerCompanies,
    setAllBuyerCompanies,
    buyerCompaniesOptions,
    setBuyerCompaniesOptions,
    eventsOptions,
    setEventsOptions,
    loadingEvents,
    setLoadingEvents,
    loadingMemberCompanies,
    setLoadingMemberCompanies,
    loadingBuyerCompanies,
    setLoadingBuyerCompanies,
    loadingExportLogs,
    setLoadingExportLogs,
    loadingLayout,
    setLoadingLayout,
    loadingB2bEvents,
    setLoadingB2bEvents,
    b2bEventsOptions,
    setB2bEventsOptions,
    b2bBuyerCompaniesOptions,
    setB2bBuyerCompaniesOptions,
    allB2bBuyerCompanies,
    setAllB2bBuyerCompanies,
    b2bBuyerUsersOptions,
    setB2bBuyerUsersOptions,
    selectedB2bEvent,
    setSelectedB2bEvent,
    selectedB2bBuyerCompanies,
    setSelectedB2bBuyerCompanies,
    selectedB2bBuyerUser,
    setSelectedB2bBuyerUser,
    selectedYear,
    setSelectedYear,
    allEventsOptions,
    setAllEventsOptions,
    billed,
    setBilled,
    selectedEventTypes,
    setSelectedEventTypes,
    yearOptions,
    reportsOptions,
    businessEventSelectProps: eventSelectProps,
    b2bEventSelectProps: eventSelectProps,
    isDownloadEnabled,
    reportI18nKeys: REPORT_I18N_KEYS,
  };
};

export default useReportsContext;
